import { z } from "zod"

export const GITHUB_RECEIPT_VIEWS = {
  jobs_over_time: "Jobs created over time",
  status: "Latest job status",
  job_count: "Job count",
  attention: "Jobs needing attention",
  published: "Published pull requests",
  usage: "Usage receipt coverage",
  cost: "Observed model cost",
  checks: "Observed check results",
  repositories: "Jobs by repository",
  recent_jobs: "Recent jobs",
} as const

export type GithubReceiptView = keyof typeof GITHUB_RECEIPT_VIEWS
export const githubReceiptViewSchema = z.enum(
  Object.keys(GITHUB_RECEIPT_VIEWS) as [
    GithubReceiptView,
    ...GithubReceiptView[],
  ],
)
const count = z.number().int().nonnegative().safe()
const amount = z.number().finite().nonnegative().nullable()
const githubReceiptTotalsSchema = z.object({
  jobs: count,
  queued: count,
  running: count,
  publishing: count,
  succeeded: count,
  failed: count,
  needs_attention: count,
  published: count,
  usage_receipts: count,
  complete_usage: count,
  cost_observed_jobs: count,
  cost_usd: amount,
  evaluation_receipts: count,
  checks_passed: count,
  checks_failed: count,
  checks_timed_out: count,
  checks_skipped: count,
  check_pass_rate: z.number().min(0).max(1).nullable(),
})
export const githubReceiptWindowSchema = z
  .object({
    start_date: z.string().datetime({ offset: true }),
    end_date: z.string().datetime({ offset: true }),
  })
  .strict()
  .refine(({ start_date, end_date }) => {
    const duration = Date.parse(end_date) - Date.parse(start_date)
    return duration > 0 && duration <= 93 * 86400_000
  }, "Select a date range of at most 93 days")

export const githubReceiptsSchema = z.object({
  schema_version: z.literal(1),
  scope: z.literal("all_organizations"),
  unit: z.literal("github_job"),
  bucket_timezone: z.literal("Etc/GMT+8"),
  organization_id: z.string().min(1),
  workspace_id: z.string().min(1),
  start_date: z.string().datetime({ offset: true }),
  end_date: z.string().datetime({ offset: true }),
  as_of: z.string().datetime({ offset: true }),
  consistency: z.literal("eventual"),
  truncated: z.boolean(),
  excluded_expired: count,
  invalid_receipts: count,
  backfill_excluded: count,
  inspected_receipts: count,
  consumed_capacity_units: z.number().finite().nonnegative(),
  totals: githubReceiptTotalsSchema,
  daily: z
    .array(
      githubReceiptTotalsSchema.extend({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      }),
    )
    .max(94),
  repositories: z
    .array(
      githubReceiptTotalsSchema.extend({ repository: z.string().max(201) }),
    )
    .max(10000),
  recent_jobs: z
    .array(
      z.object({
        job_id: z.string().min(1).max(128),
        organization_id: z.string().min(1).max(128),
        workspace_id: z.string().min(1).max(128),
        created_at: z.string().datetime({ offset: true }),
        updated_at: z.string().datetime({ offset: true }),
        repository: z.string().max(201).nullable(),
        status: z.enum([
          "queued",
          "running",
          "publishing",
          "succeeded",
          "failed",
          "needs_attention",
        ]),
        pull_request_url: z
          .string()
          .regex(
            /^https:\/\/github\.com\/[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+\/pull\/[1-9][0-9]*$/,
          )
          .nullable(),
      }),
    )
    .max(100),
})
export const scopedGithubReceiptsSchema = githubReceiptsSchema.extend({
  clerk_organization_id: z.string().min(1),
})
export type GithubReceipts = z.infer<typeof githubReceiptsSchema>
